/**
 * Where the objective stands, in words, for the kinds whose progress is not a simple count (10 §3):
 * a zone being held, a point being marked, a quest item on its way, a body to be searched, a contact
 * waiting. `working` lines are things happening this second and outrank a countdown; `standing` lines
 * are reminders and are shown only when no clock is running. Pure, and driven by the objective data.
 */

import type { MissionDef, Rect, World } from '../../sim/types';
import { TICK_RATE } from '../../sim/types';
import { mmss } from './missionRuleText';

const inside = (zone: Rect, x: number, z: number): boolean => x >= zone.x0 && x <= zone.x1 && z >= zone.z0 && z <= zone.z1;
const left = (totalSeconds: number, ticks: number): number => Math.max(0, Math.ceil(totalSeconds - ticks / TICK_RATE));

/** "HOLD THE CROSSING 2:14": the objective's own label and the time still to be held. */
function holdLine(def: MissionDef, world: World, working: boolean): string | null {
  const o = world.objective;
  const zone = def.objective.zone;
  if (def.objective.kind !== 'holdZone' || !zone || o.complete) return null;
  const clock = `${def.objective.label} ${mmss(left(def.objective.dwellSeconds ?? 0, o.holdTicks))}`;
  const here = inside(zone, world.ranger.x, world.ranger.z);
  const counting = here && (def.objective.duringWindow !== true || o.windowOpen);
  if (working) return counting ? clock : null;
  if (counting) return null;
  if (here) return `${clock} — IN POSITION, WAITING`;
  return o.holdTicks > 0 ? `${clock} — CLOCK STOPPED: GET BACK IN THE ZONE` : `${clock} — GET INTO THE ZONE`;
}

/** "HOLD HERE 0:05": he is standing in a zone that is not yet marked. */
function markLine(def: MissionDef, world: World): string | null {
  if (def.objective.kind !== 'visitZones') return null;
  const o = world.objective;
  const index = o.zoneTicks.findIndex((ticks, i) => ticks > 0 && o.zonesDone[i] !== true);
  if (index < 0) return null;
  return `HOLD HERE ${mmss(left(def.objective.dwellSeconds ?? 0, o.zoneTicks[index] ?? 0))}`;
}

function itemLine(def: MissionDef, world: World): string | null {
  const item = def.objective.questItem;
  if (!item) return null;
  const state = world.objective.item;
  if (state === 'pod') return `THE ${item.name} IS IN POD ${'ABC'[item.pod] ?? ''}`;
  if (state === 'carried') return `CARRYING THE ${item.name} — TAKE IT TO THE MARKED POINT`;
  return null;
}

/** lootTarget: he is down and still has it. killTarget / lootTarget: he is running for it. */
function targetLine(def: MissionDef, world: World): string | null {
  const o = world.objective;
  const kind = def.objective.kind;
  if ((kind !== 'lootTarget' && kind !== 'killTarget') || o.complete) return null;
  const name = def.objective.targetName ?? 'THE TARGET';
  if (o.targetFleeing) return `${name} IS RUNNING FOR IT — NORTH`;
  const man = world.enemies.find((e) => e.id === o.targetEnemyId);
  if (kind === 'lootTarget' && man && (man.state === 'dead' || man.state === 'wounded') && !o.targetLooted) {
    return `${name} IS DOWN — ${def.objective.useLabel ?? 'SEARCH THE BODY'}`;
  }
  return null;
}

function contactLine(world: World): string | null {
  return world.objective.captives.some((c) => c.state === 'waiting') ? 'THE CONTACT IS WAITING AT THE RENDEZVOUS' : null;
}

/** reachDistance with nothing counting down: how far he must be, and how far he is. */
function clearLine(def: MissionDef, world: World): string | null {
  const o = world.objective;
  const need = def.objective.clearDistance ?? 0;
  const from = def.props.find((p) => p.id === def.objective.clearFromPropId);
  if (def.objective.kind !== 'reachDistance' || !from || o.armedTick < 0 || o.complete) return null;
  const have = Math.sqrt((from.x - world.ranger.x) ** 2 + (from.z - world.ranger.z) ** 2);
  return have >= need ? `${need} M CLEAR — HOLD THERE` : `GET ${need} M CLEAR — ${Math.floor(have)} M`;
}

export function progressLine(def: MissionDef, world: World, which: 'working' | 'standing'): string | null {
  if (which === 'working') return markLine(def, world) ?? holdLine(def, world, true);
  return holdLine(def, world, false) ?? targetLine(def, world) ?? itemLine(def, world) ?? clearLine(def, world)
    ?? contactLine(world);
}
